import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  Observable,
  from as observableFrom,
  of as observableOf,
  throwError as observableThrowError,
  timer as observableTimer,
} from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { State } from '®app/ngrx';
import {
  ListPublishAction,
  PublishActionStatus,
} from '®shared/reading-list/models';

import {
  ContainerEditActionTypes,
  ResumePublishListAction,
} from '../../state/container-edit.actions';
import {
  ContainerPersistEditActionTypes,
  PersistAction,
  PersistErrorAction,
  PersistPublishListCheckAction,
  PersistResumePublishListAction,
  PersistSuccessAction,
  PublishListCheckPayload,
} from '../../state/container-persist-edit.actions';

import { UnlockQueueAndShiftAction } from './queue.actions';
import { selectQueueState } from './queue.state';
import { ReadingListEditService } from './reading-list-edit.service';

type ActionAndEtag = {
  action: PersistAction;
  etag: string;
};

export const MAX_CHECK_RETRIES = 12;
export const PUBLISH_LIST_CHECK_DELAY_MS = 5000;

@Injectable()
export class PersistenceEffects {
  private currentETag$: Observable<string | null>;

  persistAction$: Observable<
    PersistSuccessAction | PersistErrorAction | UnlockQueueAndShiftAction
  >;

  persistPublishListAction$: Observable<
    PersistPublishListCheckAction | PersistErrorAction
  >;

  persistResumePublishListAction$: Observable<PersistPublishListCheckAction>;

  persistPublishListCheckAction$: Observable<
    | PersistPublishListCheckAction
    | PersistSuccessAction
    | PersistErrorAction
    | UnlockQueueAndShiftAction
  >;

  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private readingListEditService: ReadingListEditService,
  ) {
    this.currentETag$ = this.store
      .select(selectQueueState)
      .pipe(map(state => state.etag));

    this.persistPublishListAction$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ContainerPersistEditActionTypes.PERSIST_PUBLISH_LIST),
        withLatestFrom(this.currentETag$, (action, etag) => {
          return { action, etag } as ActionAndEtag;
        }),
        switchMap(actionAndEtag => {
          const { action, etag } = actionAndEtag;
          const { listId } = action.editAction.payload;
          return this.readingListEditService.publishList(listId, etag).pipe(
            map<ListPublishAction, PublishListCheckPayload>(
              (listPublishAction: ListPublishAction) => {
                return {
                  persistAction: action,
                  id: listPublishAction.id,
                  listEtag: listPublishAction.listEtag,
                  listId,
                  status: listPublishAction.status,
                  retryCount: 0,
                  reviewUrl: listPublishAction.reviewUrl,
                };
              },
            ),
          );
        }),
        map(payload => new PersistPublishListCheckAction(payload)),
        catchError(error => this.convertToErrorAction(error)),
      ),
    );

    this.persistResumePublishListAction$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ContainerPersistEditActionTypes.PERSIST_RESUME_PUBLISH_LIST),
        map((action: PersistResumePublishListAction) => {
          const { listId, listEtag, listPublishActionId, status } = (
            action.editAction as ResumePublishListAction
          ).payload;
          const payload: PublishListCheckPayload = {
            id: listPublishActionId,
            status,
            listId,
            listEtag,
            persistAction: action,
            retryCount: 0,
          };
          return new PersistPublishListCheckAction(payload);
        }),
      ),
    );

    this.persistPublishListCheckAction$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ContainerPersistEditActionTypes.PERSIST_PUBLISH_LIST_CHECK),
        switchMap((action: PersistPublishListCheckAction) => {
          return this.readingListEditService
            .getListPublishAction(action.payload.listId, action.payload.id)
            .pipe(map(listPublishAction => ({ action, listPublishAction })));
        }),
        switchMap(actionAndListPublishAction => {
          const { status, listEtag } =
            actionAndListPublishAction.listPublishAction;
          const payload: PublishListCheckPayload =
            actionAndListPublishAction.action.payload;
          switch (status) {
            case PublishActionStatus.PROCESSING: {
              return observableTimer(PUBLISH_LIST_CHECK_DELAY_MS).pipe(
                map(_number => {
                  if (payload.retryCount === MAX_CHECK_RETRIES - 1) {
                    return new PersistErrorAction({
                      error: `Gave up waiting for successful publish after ${MAX_CHECK_RETRIES} attempts`,
                    });
                  } else {
                    const nextPayload: PublishListCheckPayload = Object.assign({
                      ...payload,
                      retryCount: payload.retryCount + 1,
                    });
                    return new PersistPublishListCheckAction(nextPayload);
                  }
                }),
              );
            }
            case PublishActionStatus.SUCCEEDED: {
              return this.reportSuccessAndUnlock({
                action: payload.persistAction,
                etag: listEtag,
              });
            }
            case PublishActionStatus.FAILED: {
              return observableOf(
                new PersistErrorAction({
                  error: `publish action status: ${PublishActionStatus.FAILED}`,
                }),
              );
            }
            default: {
              return observableThrowError(
                `Unknown publish action status: ${status}`,
              );
            }
          }
        }),
        catchError(error => this.convertToErrorAction(error)),
      ),
    );

    this.persistAction$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ContainerPersistEditActionTypes.PERSIST),
        withLatestFrom(this.currentETag$, (action, etag) => {
          return { action, etag } as ActionAndEtag;
        }),
        switchMap(actionAndEtag => this.persistActionByType(actionAndEtag)),
        switchMap(actionAndUpdatedEtag =>
          this.reportSuccessAndUnlock(actionAndUpdatedEtag),
        ),
        catchError(error => this.convertToErrorAction(error)),
      ),
    );
  }

  convertToErrorAction(error: any): Observable<PersistErrorAction> {
    return observableOf(new PersistErrorAction({ error }));
  }

  reportSuccessAndUnlock(
    actionAndEtag: ActionAndEtag,
  ): Observable<PersistSuccessAction | UnlockQueueAndShiftAction> {
    const { action, etag } = actionAndEtag;

    return observableFrom([
      new PersistSuccessAction({ persistAction: action, etag }),
      new UnlockQueueAndShiftAction({ persistAction: action, etag }),
    ]);
  }

  persistActionByType(actionAndEtag: ActionAndEtag): Observable<ActionAndEtag> {
    const { action, etag } = actionAndEtag;

    switch (action.editAction.type) {
      case ContainerEditActionTypes.CONFIRM_CREATE_NOTE_ITEM: {
        const payload = action.editAction.payload;
        return this.readingListEditService
          .createNoteItem(
            payload.listId,
            etag,
            payload.id,
            payload.student_note,
            payload.to,
          )
          .pipe(
            map(successResponse => ({ action, etag: successResponse.etag })),
          );
      }

      case ContainerEditActionTypes.CONFIRM_CREATE_RESOURCE_ITEM: {
        const payload = action.editAction.payload;
        return this.readingListEditService
          .createResourceItem(
            payload.listId,
            etag,
            payload.id,
            payload.resource.id,
            payload.to,
            payload.importance?.id,
          )
          .pipe(
            map(successResponse => ({ action, etag: successResponse.etag })),
          );
      }

      case ContainerEditActionTypes.CONFIRM_UPDATE_RESOURCE_ITEM_STUDENT_NOTE:
      case ContainerEditActionTypes.CONFIRM_UPDATE_NOTE_ITEM: {
        const payload = action.editAction.payload;
        return this.readingListEditService
          .updateItemStudentNote(
            payload.listId,
            etag,
            payload.id,
            payload.student_note,
          )
          .pipe(
            map(successResponse => ({ action, etag: successResponse.etag })),
          );
      }

      case ContainerEditActionTypes.CONFIRM_UPDATE_RESOURCE_ITEM_LIBRARY_NOTE: {
        const payload = action.editAction.payload;
        return this.readingListEditService
          .updateItemLibraryNote(
            payload.listId,
            etag,
            payload.id,
            payload.library_note,
          )
          .pipe(
            map(successResponse => ({ action, etag: successResponse.etag })),
          );
      }

      case ContainerEditActionTypes.UPDATE_RESOURCE_ITEM_IMPORTANCE: {
        const payload = action.editAction.payload;
        return this.readingListEditService
          .updateItemImportance(
            payload.listId,
            etag,
            payload.id,
            payload.importance.id,
          )
          .pipe(
            map(successResponse => ({ action, etag: successResponse.etag })),
          );
      }

      case ContainerEditActionTypes.CONFIRM_UPDATE_RESOURCE_ITEM_TAGS: {
        const payload = action.editAction.payload;
        return this.readingListEditService
          .updateItemTags(payload.listId, etag, payload.id, payload.tags)
          .pipe(
            map(successResponse => ({ action, etag: successResponse.etag })),
          );
      }

      case ContainerEditActionTypes.DELETE_RESOURCE_ITEM_STUDENT_NOTE: {
        const payload = action.editAction.payload;
        return this.readingListEditService
          .updateItemStudentNote(payload.listId, etag, payload.id, null)
          .pipe(
            map(successResponse => ({ action, etag: successResponse.etag })),
          );
      }

      case ContainerEditActionTypes.DELETE_RESOURCE_ITEM_LIBRARY_NOTE: {
        const payload = action.editAction.payload;
        return this.readingListEditService
          .updateItemLibraryNote(payload.listId, etag, payload.id, null)
          .pipe(
            map(successResponse => ({ action, etag: successResponse.etag })),
          );
      }

      case ContainerEditActionTypes.DELETE_RESOURCE_ITEM_IMPORTANCE: {
        const payload = action.editAction.payload;
        return this.readingListEditService
          .updateItemImportance(payload.listId, etag, payload.id, null)
          .pipe(
            map(successResponse => ({ action, etag: successResponse.etag })),
          );
      }

      case ContainerEditActionTypes.MOVE_ITEM: {
        const payload = action.editAction.payload;
        return this.readingListEditService
          .moveListItem(payload.listId, etag, payload.item.id, payload.to)
          .pipe(
            map(successResponse => ({ action, etag: successResponse.etag })),
          );
      }

      case ContainerEditActionTypes.MOVE_SECTION: {
        const payload = action.editAction.payload;
        return this.readingListEditService
          .moveSection(payload.listId, etag, payload.section.id, payload.to)
          .pipe(
            map(successResponse => ({ action, etag: successResponse.etag })),
          );
      }

      case ContainerEditActionTypes.CONFIRM_CREATE_SECTION: {
        const payload = action.editAction.payload;
        return this.readingListEditService
          .createSection(
            payload.listId,
            etag,
            payload.id,
            payload.title,
            payload.description,
            payload.to,
          )
          .pipe(
            map(successResponse => ({ action, etag: successResponse.etag })),
          );
      }

      case ContainerEditActionTypes.CONFIRM_UPDATE_SECTION: {
        const payload = action.editAction.payload;
        return this.readingListEditService
          .updateSection(
            payload.listId,
            etag,
            payload.id,
            payload.title,
            payload.description,
          )
          .pipe(
            map(successResponse => ({ action, etag: successResponse.etag })),
          );
      }

      case ContainerEditActionTypes.CONFIRM_DELETE_SECTION: {
        const payload = action.editAction.payload;
        return this.readingListEditService
          .deleteSection(payload.listId, etag, payload.section.id)
          .pipe(
            map(successResponse => ({ action, etag: successResponse.etag })),
          );
      }

      case ContainerEditActionTypes.CONFIRM_DELETE_ITEM: {
        const payload = action.editAction.payload;
        return this.readingListEditService
          .deleteItem(payload.listId, etag, payload.item.id)
          .pipe(
            map(successResponse => ({ action, etag: successResponse.etag })),
          );
      }
    }
  }
}
